// 메신저 미리보기 창의 docx·xlsx·pptx 에 «미리보기» 버튼을 넣는다 — 모달은 components/OfficePreviewModal.
// 설계: `patch_mm_office_preview_p2_frontend.md` §3-3 (모양은 mmSticker.setupMmStickerButton 그대로).
//
// 🔴 MM DOM 은 MM 소유다 — 동작 줄을 못 찾으면 조용히 무동작. 미리보기 창이 없을 때가 대부분이라
//    경고 로그도 남기지 않는다.
// 🔴 옵저버는 `attributes` + attributeFilter ['href', 'download'] 까지 본다 — MM 이 좌우 넘기기에서
//    같은 a 요소의 속성만 바꾸면 childList 로는 안 잡혀 버튼에 이전 파일 id 가 남는다(mmZoomLabel 과 같은 관용).
// 🔴 클릭 때는 동작 줄을 다시 읽는다 — 디바운스 사이에 넘긴 경우 만들 때 잡아 둔 값은 이전 파일이다.
import { OFFICE_LINK_SEL, readOfficeAttachment } from './mmOfficeAttachment';

export const ACTIONS_SEL = '.file-preview-modal-main-actions__actions';
export const BUTTON_CLASS = 'gw-office-preview-btn';
export const OBSERVE_DEBOUNCE_MS = 150;
// 글자가 아이콘 폭에 넘치지 않게 — className 을 복사한 뒤에도 덧붙인다.
const LABEL_STYLE = 'width:auto;white-space:nowrap;padding:0 8px';
// 이웃 다운로드 링크에 className 이 없을 때만 쓰는 최소 모양.
const FALLBACK_STYLE = 'color:rgba(255,255,255,.88);background:none;border:none;cursor:pointer;'
  + 'font-size:13px;font-weight:600;';

function removeNode(el) {
  try { if (el && el.parentNode) el.parentNode.removeChild(el); } catch (e) { /* */ }
}

// doc 당 1회 설치. 반환 = dispose(옵저버·타이머 정리 + 넣은 버튼 제거 + 표식 되돌림). 이미 설치돼 있으면 null.
export function setupMmOfficePreviewButton(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwOfficePreviewBtn) return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  const onOpen = typeof opts.onOpen === 'function' ? opts.onOpen : null;
  doc.__gwOfficePreviewBtn = true;
  let disposed = false;
  let timer = null;

  const onClick = (ev) => {
    try { ev.preventDefault(); ev.stopPropagation(); } catch (e) { /* */ }
    if (disposed || !onOpen || !isActive()) return;
    let info = null;
    try {
      const btn = ev.currentTarget || ev.target;
      const row = btn && btn.parentNode;
      info = row ? readOfficeAttachment(row) : null;
    } catch (e) { info = null; }
    if (!info) return;
    try { onOpen(info); } catch (e) { /* 모달 쪽 오류가 MM 으로 새지 않게 */ }
  };

  const makeButton = (row, info) => {
    const btn = doc.createElement('button');
    btn.setAttribute('type', 'button');
    let cls = '';
    try {
      const link = row.querySelector(OFFICE_LINK_SEL);
      if (link && typeof link.className === 'string') cls = link.className.trim();
    } catch (e) { cls = ''; }
    // 이웃 다운로드 링크와 같은 클래스 = MM 모양(크기·hover)을 따른다. 우리 표식 클래스는 늘 붙인다.
    btn.className = cls ? (cls + ' ' + BUTTON_CLASS) : BUTTON_CLASS;
    btn.setAttribute('data-file-id', info.fileId);
    btn.setAttribute('aria-label', '미리보기');
    btn.setAttribute('title', '게이트웨이에서 미리보기');
    btn.textContent = '미리보기';
    btn.style.cssText = cls ? LABEL_STYLE : (FALLBACK_STYLE + LABEL_STYLE);
    btn.addEventListener('click', onClick);
    return btn;
  };

  // 동작 줄 한 개를 지금 파일에 맞춘다 — 없으면 지우고, 같은 파일이면 두고, 다르면 갈아 끼운다.
  const syncRow = (row) => {
    const info = readOfficeAttachment(row);
    const mine = row.querySelectorAll('button.' + BUTTON_CLASS);
    let keep = null;
    for (let i = 0; i < mine.length; i += 1) {
      const btn = mine[i];
      if (!keep && info && btn.getAttribute('data-file-id') === info.fileId) keep = btn;
      else removeNode(btn);
    }
    if (!info || keep) return;
    row.insertBefore(makeButton(row, info), row.firstChild);   // 줄 맨 앞
  };

  const apply = () => {
    if (disposed || !isActive()) return;
    let rows = [];
    try { rows = doc.querySelectorAll(ACTIONS_SEL); } catch (e) { return; }
    for (let i = 0; i < rows.length; i += 1) {
      try { syncRow(rows[i]); } catch (e) { /* MM DOM 변동 — 무동작 */ }
    }
  };

  const kick = () => {
    if (disposed || timer) return;   // 디바운스 — MM 은 프레임마다 변이를 낸다
    timer = setTimeout(() => {
      timer = null;
      if (disposed) return;
      apply();
    }, OBSERVE_DEBOUNCE_MS);
  };

  apply();   // 설치 즉시 1회(이미 열려 있는 미리보기 창도 잡는다)

  let obs = null;
  try {
    const MO = (doc.defaultView && doc.defaultView.MutationObserver)
      || (typeof window !== 'undefined' ? window.MutationObserver : null);
    if (MO) {
      // 🔴 미리보기 창이 아니라 doc.body 에 건다 — 창은 열 때마다 새로 만들어진다.
      obs = new MO(kick);
      obs.observe(doc.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'download'],
      });
    }
  } catch (e) { obs = null; }

  return function dispose() {
    if (disposed) return;
    disposed = true;                 // 남은 콜백·타이머는 여기서부터 무동작
    try { if (obs) obs.disconnect(); } catch (e) { /* */ }
    if (timer) { clearTimeout(timer); timer = null; }
    try {
      const list = doc.querySelectorAll('button.' + BUTTON_CLASS);
      for (let i = 0; i < list.length; i += 1) removeNode(list[i]);
    } catch (e) { /* */ }
    try { doc.__gwOfficePreviewBtn = false; } catch (e) { /* */ }
  };
}
